// 浏览器文件系统适配层。
// 这里用最小化的结构类型描述 File System Access API，避免依赖特定 TS 版本内置的类型定义。

export interface WritableLike {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}

export interface FileHandleLike {
  kind: "file";
  name: string;
  getFile(): Promise<File>;
  createWritable(options?: { keepExistingData?: boolean }): Promise<WritableLike>;
}

export interface DirHandleLike {
  kind: "directory";
  name: string;
  values(): AsyncIterableIterator<FileHandleLike | DirHandleLike>;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<DirHandleLike>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileHandleLike>;
}

export interface SourceItem {
  /** 相对路径（用于日志与预览下拉） */
  path: string;
  name: string;
  file: File;
  /** 文件夹模式下才有，用于原地覆盖 */
  handle?: FileHandleLike;
}

export function isFileSystemAccessSupported(): boolean {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

/** 让用户选一个文件夹（读写权限） */
export async function pickDirectory(): Promise<DirHandleLike | null> {
  if (!isFileSystemAccessSupported()) return null;
  const handle = await window.showDirectoryPicker!({ mode: "readwrite", id: "watermark-src" });
  const dir = handle as unknown as DirHandleLike;
  if (typeof dir.values !== "function") throw new Error("当前浏览器不支持目录遍历，请改用「选择图片文件」");
  return dir;
}

/** 递归收集图片，跳过输出目录与隐藏目录 */
export async function collectImages(
  dir: DirHandleLike,
  extensions: Set<string>,
  skipDirs: Set<string>,
  limit = 5000,
): Promise<SourceItem[]> {
  const out: SourceItem[] = [];

  async function walk(current: DirHandleLike, prefix: string): Promise<void> {
    if (out.length >= limit) return;
    for await (const entry of current.values()) {
      if (out.length >= limit) return;
      if (entry.kind === "directory") {
        if (skipDirs.has(entry.name) || entry.name.startsWith(".")) continue;
        await walk(entry as DirHandleLike, `${prefix}${entry.name}/`);
      } else {
        const dot = entry.name.lastIndexOf(".");
        const ext = dot >= 0 ? entry.name.slice(dot).toLowerCase() : "";
        if (!extensions.has(ext)) continue;
        const file = await (entry as FileHandleLike).getFile();
        out.push({ path: `${prefix}${entry.name}`, name: entry.name, file, handle: entry as FileHandleLike });
      }
    }
  }

  await walk(dir, "");
  out.sort((a, b) => a.path.localeCompare(b.path));
  return out;
}

export async function ensureSubDir(parent: DirHandleLike, name: string): Promise<DirHandleLike> {
  return parent.getDirectoryHandle(name, { create: true });
}

/** 写入文件（同目录同名则覆盖） */
export async function writeFile(dir: DirHandleLike, name: string, blob: Blob): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/** 原地覆盖：写入会先落到交换文件，close() 时才替换原文件（等价于「临时文件 + 改名」） */
export async function writeInPlace(handle: FileHandleLike, blob: Blob): Promise<void> {
  const writable = await handle.createWritable({ keepExistingData: false });
  await writable.write(blob);
  await writable.close();
}

/** 逐张下载（文件/示例图模式下使用） */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
